import toast from 'react-hot-toast';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getPlayerName } from '../../util/device-id';
import { useTranslation } from 'react-i18next';
import { addPlayerToGame, cleanupExpiredGames, createGame, ensureGameExpiration, fetchPictureSets, fetchWordLists, MAX_PLAYERS_PER_GAME } from '../../firebase/firebase-db.ts';
import { Button } from '../../components/home/buttons';
import type { GameConfigDto } from '@/game/core/game.ts';
import type { PictureSetDto } from '@/game/picture-set.ts';
import type { ListInfoDto } from '@/game/core/shiritori-word.ts';
import { Card } from '@/components/home/card.tsx';
import { Select } from '@/components/home/select.tsx';
import { Input } from '@/components/home/input.tsx';
import { isValidGameId } from '@/util/index.ts';

const MIN_TURN_TIMER_SECONDS = 15;

interface GameConfigureRouteProps {
}

export function GameConfigureRoute({ }: GameConfigureRouteProps) {
    const navigate = useNavigate();
    const gameId = useParams<{ gameId: string }>().gameId || '';
    const [isLoading, setIsLoading] = useState(true);

    const [pictureSetOptions, setPictureSetOptions] = useState<PictureSetDto[]>([]);
    const [wordListOptions, setWordListOptions] = useState<ListInfoDto[]>([]);
    const [pictureSetChoice, setPictureSetChoice] = useState<PictureSetDto>();
    const [wordListChoice, setWordListChoice] = useState<ListInfoDto>();

    const [expectedPlayers, setExpectedPlayers] = useState(1);
    const [boardSize, setBoardSize] = useState(3);
    const [allowHomophones, setAllowHomophones] = useState(true);
    const [allowHints, setAllowHints] = useState(true);
    const [turnTimerMode, setTurnTimerMode] = useState<'none' | 'timer'>('none');
    const [turnTimerSeconds, setTurnTimerSeconds] = useState('30');
    const { t } = useTranslation();

    useEffect(() => {
        void Promise.allSettled([
            ensureGameExpiration(gameId),
            cleanupExpiredGames(),
        ]);

        Promise.all([
            fetchPictureSets(),
            fetchWordLists()
        ]).then(([pictureSets, wordLists]) => {
            const pictures = Object.values(pictureSets);
            const words = Object.values(wordLists);
            setPictureSetOptions(pictures);
            setWordListOptions(words);

            setWordListChoice(words[0]);
            setPictureSetChoice(pictures[0]);

            setIsLoading(false);
        }).catch((error) => {
            console.log('something happened', error);
        })
    }, [gameId]);

    const isValidSetup = useMemo(() => {
        const playerName = getPlayerName();
        return isValidGameId(gameId) && Boolean(playerName?.trim()?.length >= 4);
    }, [gameId]);

    async function onCreateGame() {
        if (isValidSetup && pictureSetChoice && wordListChoice) {
            const config: GameConfigDto = {
                expectedPlayers: Math.max(1, expectedPlayers),
                boardSize,
                pictureSetId: pictureSetChoice.pictureSetId,
                allowHomophones,
                allowHints,
                listWordSetId: wordListChoice.listWordSetId,
                turnTimer: turnTimerMode === 'timer'
                    ? { mode: 'timer', seconds: Math.max(MIN_TURN_TIMER_SECONDS, Number(turnTimerSeconds) || MIN_TURN_TIMER_SECONDS) }
                    : { mode: 'none' }
            };

            try {
                setIsLoading(true);
                const result = await createGame(gameId, config);
                if (result.committed) {
                    await addPlayerToGame(gameId, getPlayerName());
                    navigate(`/game/${gameId}`);
                } else {
                    toast.error(t('configure.toastCreateError'), {
                        position: 'bottom-center',
                        duration: 2500,
                    });
                }
            } catch (error) {
                setIsLoading(false);
                console.log('[onCreateGame]', error);
                toast.error(t('configure.toastTryAgain'), {
                    position: 'bottom-center',
                    duration: 2500,
                });
            }
        }
    }

    return (
        <div className="w-full h-full flex items-center justify-center">
            <Card header={t('configure.header')} className="container max-w-md">
                <div className="flex flex-col gap-1">
                    <label className="font-semibold opacity-80">{t('configure.labelExpectedPlayers')}</label>
                    <Select value={expectedPlayers} onChange={(e) => setExpectedPlayers(Number(e.target.value))}>
                        {Array(MAX_PLAYERS_PER_GAME).fill(null).map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                    </Select>
                </div>
                <div className="flex flex-col gap-1">
                    <label className="font-semibold opacity-80">{t('configure.labelBoardSize')}</label>
                    <Select value={boardSize} onChange={(e) => setBoardSize(Number(e.target.value))}>
                        <option value={3}>3 x 3</option>
                        <option value={5}>5 x 5</option>
                        <option value={7}>7 x 7</option>
                    </Select>
                </div>
                {/*
                <div className="flex flex-col gap-1">
                    <label className="font-semibold opacity-80">{t('configure.labelPictureSet')}</label>
                    <Select value={pictureSetChoice?.pictureSetId} onChange={(e) => setPictureSetChoice(pictureSetOptions.find(x => x.pictureSetId === e.target.value))}>
                        {pictureSetOptions.map(({ pictureSetId, displayName }) => <option key={pictureSetId} value={pictureSetId}>{displayName}</option>)}
                    </Select>
                </div>
                */}
                {/*
                <div className="flex flex-col gap-1">
                    <label className="font-semibold opacity-80">{t('configure.labelWordList')}</label>
                    <Select value={wordListChoice?.listWordSetId} onChange={(e) => setWordListChoice(wordListOptions.find(x => x.listWordSetId === e.target.value))}>
                        {wordListOptions.map(({ listWordSetId, displayName }) => <option key={listWordSetId} value={listWordSetId}>{displayName}</option>)}
                    </Select>
                </div>
                */}
                <div className="flex flex-col gap-2">
                    <label className="font-semibold opacity-80">{t('configure.labelTurnTimer')}</label>
                    <div className="flex gap-2">
                        <Select value={turnTimerMode} onChange={(e) => setTurnTimerMode(e.target.value as 'none' | 'timer')} className="w-full sm:w-1/2 px-3 py-2 font-semibold">
                            <option value="none">{t('configure.optionNoTimer')}</option>
                            <option value="timer">{t('configure.optionTimer')}</option>
                        </Select>
                        {turnTimerMode === 'timer' && (
                            <div className="mb-4 flex sm:flex-1 items-center">
                                <Input
                                    type="number"
                                    min={MIN_TURN_TIMER_SECONDS}
                                    value={turnTimerSeconds}
                                    onChange={(event) => setTurnTimerSeconds(event.target.value)}
                                    onBlur={() => setTurnTimerSeconds(String(Math.max(MIN_TURN_TIMER_SECONDS, Number(turnTimerSeconds) || MIN_TURN_TIMER_SECONDS)))}
                                    post={t('configure.unitSeconds')}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                    <input id="allow-hints" type="checkbox" checked={allowHints} onChange={(event) => setAllowHints(event.target.checked)} className="h-4 w-4 cursor-pointer accent-primary" />
                    <label htmlFor="allow-hints" className="font-semibold opacity-80 cursor-pointer">
                        {t('configure.labelAllowHints')}
                    </label>
                </div>
                {/*
                <div className="flex items-center gap-2 col-span-2">
                    <input id="homophones" type="checkbox" checked={allowHomophones} onChange={(e) => setAllowHomophones(e.target.checked)} className="h-4 w-4" />
                    <label htmlFor="homophones" className="font-semibold opacity-80">{t('configure.labelAllowHomophones')}</label>
                </div>
                */}
                <Button hotkey={{ key: 'Enter', display: '⏎', allowInInput: true }} isLoading={isLoading} className="w-full mt-10" onClick={onCreateGame}>{t('configure.btnStartGame')}</Button>
            </Card>
        </div>
    );
}
